import { useTheme } from "./useTheme";
import type { Theme } from "./types";
import { cn } from "../utils/cn";

// ── Types ─────────────────────────────────────────────────────────────────────

export interface ThemeToggleProps {
  variant?: "icon-only" | "labeled" | "three-way";
  size?: "sm" | "md" | "lg";
  className?: string;
}

// ── Size config ───────────────────────────────────────────────────────────────

const SIZE_MAP = {
  sm: { btn: "h-7 w-7", icon: 14, text: "text-xs", pill: "h-7 px-2.5 text-xs gap-1" },
  md: { btn: "h-9 w-9", icon: 16, text: "text-sm", pill: "h-9 px-3 text-sm gap-1.5" },
  lg: { btn: "h-11 w-11", icon: 18, text: "text-base", pill: "h-11 px-4 text-base gap-2" },
};

// ── Icons ─────────────────────────────────────────────────────────────────────

function SunIcon({ size }: { size: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="4" />
      <line x1="12" y1="2" x2="12" y2="4" />
      <line x1="12" y1="20" x2="12" y2="22" />
      <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
      <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
      <line x1="2" y1="12" x2="4" y2="12" />
      <line x1="20" y1="12" x2="22" y2="12" />
      <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
      <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
    </svg>
  );
}

function MoonIcon({ size }: { size: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
    </svg>
  );
}

function MonitorIcon({ size }: { size: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="2" y="3" width="20" height="14" rx="2" />
      <line x1="8" y1="21" x2="16" y2="21" />
      <line x1="12" y1="17" x2="12" y2="21" />
    </svg>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

export function ThemeToggle({
  variant = "icon-only",
  size = "md",
  className,
}: ThemeToggleProps) {
  const { resolvedTheme, theme, toggleTheme, setTheme } = useTheme();
  const s = SIZE_MAP[size];

  if (variant === "three-way") {
    const options: { value: Theme; label: string; Icon: typeof SunIcon }[] = [
      { value: "light",  label: "Light",  Icon: SunIcon },
      { value: "system", label: "System", Icon: MonitorIcon },
      { value: "dark",   label: "Dark",   Icon: MoonIcon },
    ];

    return (
      <div
        className={cn(
          "inline-flex rounded-pill border border-border-default bg-surface-raised p-0.5",
          className,
        )}
        role="group"
        aria-label="Theme selector"
      >
        {options.map(({ value, label, Icon }) => {
          const active = theme === value;
          return (
            <button
              key={value}
              type="button"
              onClick={() => setTheme(value)}
              aria-pressed={active}
              aria-label={`${label} theme`}
              className={cn(
                "inline-flex items-center justify-center rounded-pill font-medium",
                "transition-all duration-150 cursor-pointer",
                s.pill,
                active
                  ? "bg-brand-purple text-white shadow-sm"
                  : "text-content-secondary hover:text-content-primary",
              )}
            >
              <Icon size={s.icon} />
              <span>{label}</span>
            </button>
          );
        })}
      </div>
    );
  }

  const isDark = resolvedTheme === "dark";

  return (
    <button
      type="button"
      onClick={toggleTheme}
      aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
      className={cn(
        "inline-flex items-center justify-center rounded-full border border-border-default",
        "bg-surface-raised text-content-secondary",
        "hover:text-content-primary hover:bg-surface-overlay",
        "transition-all duration-150 cursor-pointer",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-purple",
        "focus-visible:ring-offset-2 focus-visible:ring-offset-surface",
        variant === "labeled"
          ? cn(s.pill, "w-auto rounded-pill")
          : s.btn,
        className,
      )}
    >
      <span
        className="transition-transform duration-300"
        style={{ transform: isDark ? "rotate(0deg)" : "rotate(180deg)" }}
      >
        {isDark ? <SunIcon size={s.icon} /> : <MoonIcon size={s.icon} />}
      </span>
      {variant === "labeled" && (
        <span className={cn(s.text, "font-medium")}>
          {isDark ? "Light" : "Dark"}
        </span>
      )}
    </button>
  );
}

ThemeToggle.displayName = "ThemeToggle";
